import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Target, Eye, Heart, ArrowRight, Sparkles, Zap, Star, Award, Rocket, Users } from "lucide-react";

const About = () => {
  const [countersVisible, setCountersVisible] = useState(false);
  const countersRef = useRef(null);
  const observerRef = useRef(null);

  const stats = [
    { value: 4, suffix: "", label: "Campaigns Managed" },
    { value: 40, suffix: "+", label: "Agents Trained" },
  ];

  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description:
        "To deliver exceptional outsourcing solutions that drive business growth and operational efficiency for our clients worldwide.",
    },
    {
      icon: Eye,
      title: "Our Vision",
      description:
        "To become the most trusted and innovative BPO partner, recognized for precision, quality, and transformative business impact.",
    },
    {
      icon: Heart,
      title: "Our Values",
      description:
        "Excellence, Integrity, Innovation, and Client Success form the foundation of everything we do at NUVEXA.",
    },
  ];

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".reveal, .slide-left, .slide-right, .zoom-reveal, .flip-in, .blur-in").forEach((el) => {
      observerRef.current.observe(el);
    });

    // Counter observer
    const counterObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setCountersVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (countersRef.current) {
      counterObserver.observe(countersRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
      counterObserver.disconnect();
    };
  }, []);

  const AnimatedCounter = ({ value, suffix }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!countersVisible) return;

      let start = 0;
      const end = value;
      const duration = 2000;
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(timer);
    }, [countersVisible, value]);

    return (
      <div className="flex items-center justify-center">
        <span className="text-4xl sm:text-5xl font-extrabold text-[#6DB432] group-hover:scale-110 transition-transform duration-300">
          {count}
        </span>
        {suffix && (
          <span className="text-3xl sm:text-4xl font-bold text-[#8BC064] animate-pulse ml-1">
            {suffix}
          </span>
        )}
      </div>
    );
  };

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section 
        className="py-24 relative overflow-hidden"
        style={{
          backgroundImage: `url('/images/entrance.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#353537]/95 to-[#353537]/70" />
        
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="particle" style={{ top: '10%', left: '10%', animationDelay: '0s' }} />
          <div className="particle" style={{ top: '20%', left: '80%', animationDelay: '2s' }} />
          <div className="particle" style={{ top: '60%', left: '20%', animationDelay: '4s' }} />
          <div className="particle" style={{ top: '70%', left: '70%', animationDelay: '1s' }} />
          <div className="particle" style={{ top: '40%', left: '50%', animationDelay: '3s' }} />
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-20 right-20 animate-float opacity-30">
          <Sparkles className="w-10 h-10 text-[#6DB432] animate-spin" style={{ animationDuration: '8s' }} />
        </div>
        <div className="absolute bottom-40 right-40 animate-float delay-500 opacity-30">
          <Zap className="w-8 h-8 text-[#8BC064] heartbeat" />
        </div>
        <div className="absolute top-40 left-[60%] animate-float delay-300 opacity-25">
          <Star className="w-6 h-6 text-[#6DB432] animate-pulse" />
        </div>
        <div className="absolute bottom-20 left-20 animate-float opacity-20" style={{ animationDelay: '1s' }}>
          <Award className="w-8 h-8 text-[#8BC064]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl stagger-children">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2 bouncy-entrance">
              <span className="w-2 h-2 rounded-full bg-[#6DB432] animate-pulse" />
              About Us
              <span className="w-8 h-px bg-gradient-to-r from-[#6DB432] to-transparent" />
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              <span className="inline-block hover:scale-105 transition-transform duration-300 text-reveal" style={{ animationDelay: '0.2s' }}>Building Success</span>
              <br />
              <span className="gradient-text-animated neon-text" style={{ animationDelay: '0.4s' }}>Through Precision</span>
            </h1>
            <p className="mt-6 text-gray-300 text-lg sm:text-xl leading-relaxed text-reveal" style={{ animationDelay: '0.6s' }}>
              NUVEXA LLC is a leading BPO and call center solutions provider,
              committed to helping businesses achieve operational excellence
              through innovative outsourcing services.
            </p>
          </div>
        </div>
        
        {/* Morphing shape decoration */}
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-gradient-to-br from-[#6DB432]/10 to-[#8BC064]/5 -translate-y-1/2 morph-shape" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-[#6DB432]/5 rounded-full blur-3xl animate-pulse" />
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-[#0f0f10] relative overflow-hidden">
        {/* Animated corner decorations */}
        <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-[#6DB432]/20 rounded-tl-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-[#6DB432]/20 rounded-br-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Spotlight sweep effect */}
        <div className="absolute inset-0 spotlight-sweep opacity-30" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="slide-left">
              <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2">
                <Rocket className="w-4 h-4 animate-bounce" />
                Who We Are
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white leading-tight">
                A Partner You Can <span className="gradient-text-animated">Trust</span>
              </h2>
              <div className="mt-6 space-y-4 text-gray-400 leading-relaxed">
                <p className="hover:text-gray-300 transition-colors duration-300 hover:translate-x-2 transform">
                  Founded with a vision to transform business operations, NUVEXA
                  LLC has grown to become a trusted name in the BPO industry.
                  Our commitment to excellence and precision sets us apart.
                </p>
                <p className="hover:text-gray-300 transition-colors duration-300 hover:translate-x-2 transform">
                  We specialize in providing comprehensive call center and
                  back-office solutions that help businesses focus on their core
                  competencies while we handle the rest.
                </p>
                <p className="hover:text-gray-300 transition-colors duration-300 hover:translate-x-2 transform">
                  Our team of dedicated professionals brings expertise,
                  innovation, and a customer-first approach to every project we
                  undertake.
                </p>
              </div>
            </div>
            <div className="relative slide-right">
              {/* Funky frame container */}
              <div className="relative group">
                {/* Main image container */}
                <div className="relative overflow-hidden rounded-2xl">
                  {/* Corner accent shapes */}
                  <div className="absolute -top-2 -left-2 w-10 h-10 border-t-3 border-l-3 border-[#6DB432] rounded-tl-xl z-10" />
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 border-b-3 border-r-3 border-[#6DB432] rounded-br-xl z-10" />
                  
                  <img
                    src="/images/image-9.jpg"
                    alt="NUVEXA Branded Workspace"
                    className="w-full h-auto object-contain rounded-2xl brightness-95 group-hover:brightness-110 transition-all duration-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Counters */}
      <section
        ref={countersRef}
        className="py-24 bg-[#353537]/50 relative overflow-hidden"
        data-testid="counters-section"
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#6DB432]/30 to-transparent" />
          <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#6DB432]/10 to-transparent" />
          <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#6DB432]/10 to-transparent" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-center gap-16 md:gap-24">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                data-testid={`counter-${index}`}
                className="text-center group cursor-default"
              >
                <div className="relative flex items-center justify-center">
                  {/* Animated ring */}
                  <div className="absolute -inset-8 border-2 border-dashed border-[#6DB432]/20 rounded-full animate-spin" style={{ animationDuration: '20s' }} />
                  <div className="absolute -inset-4 border border-[#6DB432]/30 rounded-full group-hover:scale-110 transition-transform duration-500" />
                  
                  {/* Number container */}
                  <div className="relative bg-[#1a1a1b]/80 rounded-full w-32 h-32 md:w-40 md:h-40 flex items-center justify-center group-hover:bg-[#6DB432]/10 transition-all duration-500">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-[#6DB432]/5 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </div>
                <p className="mt-6 text-gray-300 text-lg font-semibold uppercase tracking-wider group-hover:text-[#6DB432] transition-colors duration-300">
                  {stat.label}
                </p>
              </div>
            ))}
            
            {/* Connecting line between stats */}
            <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-px bg-gradient-to-r from-[#6DB432]/50 via-[#8BC064] to-[#6DB432]/50" style={{ width: '120px' }} />
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-24 bg-[#0f0f10] relative overflow-hidden">
        {/* Decorative X mark */}
        <img
          src="/images/x-mark.png"
          alt=""
          className="absolute -right-10 top-10 h-[400px] w-auto opacity-[0.1] pointer-events-none object-contain floating-x"
        />
        
        {/* Animated background grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(#6DB432 1px, transparent 1px), linear-gradient(90deg, #6DB432 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 zoom-reveal">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2 justify-center">
              <Zap className="w-4 h-4 heartbeat" />
              What Drives Us
              <Zap className="w-4 h-4 heartbeat" style={{ animationDelay: '0.5s' }} />
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">
              Our Core <span className="gradient-text-animated neon-text">Principles</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((item, index) => (
              <div
                key={item.title}
                data-testid={`value-card-${index}`}
                className="flip-in glass-card border border-white/5 hover:border-[#6DB432]/50 p-8 rounded-xl pop-out group stacked-cards"
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                <div className="w-14 h-14 rounded-lg bg-[#6DB432]/20 flex items-center justify-center mb-6 group-hover:bg-[#6DB432]/40 transition-all duration-500 group-hover:scale-110">
                  <item.icon className="w-7 h-7 text-[#6DB432]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-[#6DB432] transition-colors duration-300 underline-grow">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                  {item.description}
                </p>
                {/* Hover indicator */}
                <div className="mt-4 flex items-center text-[#6DB432] opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-2">
                  <span className="text-sm font-medium">Learn more</span>
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-2 transition-transform duration-300" />
                </div>
                
                {/* Shimmer effect on hover */}
                <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#6DB432]/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Culture Banner */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed color-shift"
          style={{
            backgroundImage:
              'url("/images/image-11.jpg")',
          }}
        >
          <div className="absolute inset-0 bg-[#353537]/80" />
        </div>
        
        {/* Animated overlay patterns */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-[#6DB432] rounded-full animate-ping" style={{ animationDuration: '4s' }} />
            <div className="absolute bottom-1/4 right-1/4 w-24 h-24 border border-[#8BC064] rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '1s' }} />
          </div>
        </div>
        
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <div className="blur-in stagger-children">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2 justify-center">
              <Star className="w-4 h-4 heartbeat" />
              Our Culture
              <Users className="w-4 h-4 animate-bounce" />
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              <span className="inline-block hover:scale-105 transition-transform duration-300">Where Excellence Meets</span>
              <br />
              <span className="gradient-text-animated text-glow">Opportunity</span>
            </h2>
            <p className="mt-6 text-gray-300 text-lg max-w-2xl mx-auto">
              At NUVEXA, we foster a culture of continuous learning, innovation,
              and professional growth. Our team is our greatest asset.
            </p>
            <Link to="/careers" className="inline-block mt-8">
              <Button
                data-testid="join-team-btn"
                className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-8 py-3 rounded-md btn-glow btn-shine group jello-hover liquid-btn animate-pulse-glow"
              >
                Join Our Team
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0f0f10] relative overflow-hidden">
        {/* Animated floating elements */}
        <div className="absolute top-10 left-10 w-20 h-20 border border-[#6DB432]/20 rounded-lg float-card opacity-30" />
        <div className="absolute bottom-20 right-20 w-16 h-16 border border-[#8BC064]/20 rounded-full float-card opacity-30" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-[#6DB432]/10 rounded-full animate-ping opacity-20" style={{ animationDuration: '3s' }} />
        
        {/* Neon lines */}
        <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-[#6DB432]/20 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center zoom-reveal">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 hover:scale-105 transition-transform duration-300 inline-block">
              Ready to Partner with Us?
            </h2>
            <p className="gradient-text-animated text-lg font-bold tracking-widest uppercase mb-8 neon-text">
              Powered by PRECISION
            </p>
            <Link to="/contact">
              <Button
                data-testid="partner-cta-btn"
                className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-10 py-4 text-lg rounded-md animate-pulse-glow btn-shine rubberBand-hover particle-burst"
              >
                Contact Us Today
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
